import { httpsCallable } from 'firebase/functions';
import { repositories } from '../../../data/repositories';
import type { Customer, Address } from '../../../types/customer';
import { functions } from '../../../lib/firebase';
import { normalizeMobile } from '../../../../shared/phone';

/**
 * True when the string is a plausible staff email. The Firestore rule on
 * `/customers/{id}` reads `emailLower() in assignedStaff` for non-admin
 * staff — so anything that gets into `assignedStaff` without an `@` is
 * dead weight at best, and at worst (e.g. `customer-self-setup` from the
 * customer-facing payment flow) it can poison the field so that the only
 * "staff" listed is a synthetic actor that no real user can ever match.
 *
 * The authoritative sanitization runs server-side in the `syncCustomer`
 * Cloud Function (same rule, vendored there); these client copies exist for
 * unit tests and any client-side display filtering.
 */
export const isStaffEmail = (s: string | undefined | null): s is string =>
  typeof s === 'string' && s.includes('@');

/**
 * Take a candidate `assignedStaff` array, drop anything that isn't a valid
 * staff email, and dedupe. Preserves array shape; empty result is valid (the
 * Firestore rule will then deny all non-admin reads until a real staff merges
 * back in, which is the correct fail-safe for an orphan customer doc).
 *
 * Exported for unit testing — the public service surface is unchanged.
 */
export const sanitizeAssignedStaff = (entries: ReadonlyArray<string | undefined>): string[] =>
  Array.from(new Set(entries.filter(isStaffEmail)));

/**
 * Loosely-typed shape for the raw `customer` blob that arrives from quotes
 * and orders. Includes flat + nested forms because both shapes are in
 * the wild (CustomerForm submits flat; order synthesis submits nested).
 */
interface CustomerInput {
  mobile?: string;
  name?: string;
  email?: string;
  postal?: string;
  blk?: string;
  street?: string;
  building?: string;
  unit?: string;
  primaryAddress?: Address;
}

export const customerService = {
  async fetchCustomers(staffEmail: string, role: string | null): Promise<Customer[]> {
    if (role === 'ADMIN') {
      return await repositories.customers.list();
    }
    return await repositories.customers.findByStaff(staffEmail);
  },

  async fetchCustomerById(id: string): Promise<Customer | null> {
    return await repositories.customers.findById(id);
  },

  /**
   * Persist edits to an existing customer record. Direct client write — the
   * `/customers` update rule allows ADMIN or an assigned staff member, and the
   * `save` merge preserves `assignedStaff`. The doc id (= normalized mobile) is
   * the customer's identity and is intentionally NOT editable here (changing it
   * would re-key the doc and desync the quote/order links that embed mobile).
   */
  async updateCustomer(customer: Customer): Promise<void> {
    await repositories.customers.save(customer);
  },

  /**
   * Remove a customer record. ADMIN only — enforced by the `/customers`
   * Firestore rule (`allow delete: if isAdmin()`); `decideCustomerDelete`
   * mirrors that on the client and adds the advisory has-orders block.
   *
   * Past quotes and orders each snapshot the customer at creation, so their
   * documents still render correctly afterwards; what is lost is the live
   * lookup.
   */
  async deleteCustomer(id: string): Promise<void> {
    await repositories.customers.delete(id);
  },

  /**
   * Create a customer directly, from the customers UI rather than as a
   * side effect of saving a quote.
   *
   * Goes through the same `syncCustomer` Cloud Function as the quote path so
   * there is exactly ONE upsert implementation: the CF resolves the caller
   * from request auth and stamps `assignedStaff`, without which the new
   * record would be invisible to its own creator under the `/customers` read
   * rule.
   *
   * Unlike `syncCustomerFromEntity` this deliberately does NOT swallow
   * errors. There, a sync failure must never fail an already-saved quote;
   * here the write IS the user's action, so a failure has to reach them.
   *
   * Because the CF upserts, creating against a mobile that already exists
   * MERGES into that record — the documented dedup behaviour. The caller
   * uses the returned `created` flag to tell the operator which happened.
   */
  async createCustomer(customerData: CustomerInput): Promise<{ created: boolean; id: string }> {
    const customerId = normalizeMobile(customerData.mobile ?? '');
    if (!customerId) throw new Error('A mobile number is required to create a customer.');
    const res = await syncCustomerCallable({ customerId, customerData });
    return { created: res.data.created, id: res.data.customerId };
  },

  /**
   * Look up an existing customer by mobile number in any format.
   * Normalizes the input before lookup (strips spaces/dashes/+65 prefix) so
   * "9123 4567", "+65 9123-4567", and "91234567" all resolve to the same
   * record. Returns null if no match. Used by CustomerForm for soft-warn
   * duplicate detection as the rep types.
   */
  async findByMobile(rawMobile: string): Promise<Customer | null> {
    const id = normalizeMobile(rawMobile);
    if (!id) return null;
    return await repositories.customers.findById(id);
  },

  // The acting staff is resolved server-side from request auth by the
  // syncCustomer CF — a client-supplied actor was only ever used by the
  // deleted offline mirror, so the parameter is gone.
  async syncCustomerFromEntity(customerData: CustomerInput): Promise<void> {
    const customerId = normalizeMobile(customerData.mobile ?? '');
    if (!customerId) return;

    // Route through the syncCustomer Cloud Function so that the Admin SDK
    // can read/merge an existing customer doc even when the calling staff
    // isn't on its assignedStaff array (which would otherwise throw
    // permission-denied at findById and bubble up as a fake "quote save
    // failed" error). The function also adds the caller to assignedStaff
    // atomically so they see the customer in /customers next. The offline
    // localStorage upsert mirror (buildNewCustomerDoc / mergeCustomerDoc)
    // was deleted in the dual-path retirement (Phase 2b) — the CF is the
    // single upsert implementation, exercised locally via `npm run dev:emu`.
    //
    // Best-effort by design — never let a sync failure block the quote
    // save. The quote has already been written by the caller before this
    // line runs, and the customer record is recoverable from quote data
    // if sync drops. Failures are logged for ops, never surfaced to the rep.
    try {
      await syncCustomerCallable({ customerId, customerData });
    } catch (e) {
      console.warn(
        '[syncCustomer] cloud sync failed; customer record not updated. ' +
          'The quote itself is unaffected. Error:',
        e,
      );
    }
  },
};

const syncCustomerCallable = httpsCallable<
  { customerId: string; customerData: CustomerInput },
  { ok: true; created: boolean; customerId: string }
>(functions, 'syncCustomer');
